import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { STATE_SLUG_TO_ABBR, circuitSlug } from "@/lib/seo";
import { sportFromSlug } from "@/lib/sportRoutes";
import Landing from "@/pages/Landing";
import SlugRedirect from "@/pages/SlugRedirect";

/**
 * Resolves the second segment of `/:sport/:slug` in priority order:
 *   1. US state (e.g. /basketball/texas)      -> state landing page
 *   2. Known circuit (e.g. /basketball/prepgirlshoops) -> circuit landing page
 *   3. Otherwise                               -> Pro custom-listing URL resolver
 */
function CircuitOrSlug() {
    const { sport, slug } = useParams();
    const sportName = sportFromSlug(sport);
    const [resolved, setResolved] = useState("loading"); // "loading" | {circuit} | "redirect"

    useEffect(() => {
        if (!sportName) { setResolved("redirect"); return undefined; }
        let alive = true;
        const target = circuitSlug(slug);
        Promise.all([
            api.get(`/circuits/config?sport=${encodeURIComponent(sportName)}`).then((r) => r.data).catch(() => null),
            api.get(`/circuits/user?sport=${encodeURIComponent(sportName)}`).then((r) => r.data).catch(() => []),
        ]).then(([cfg, users]) => {
            if (!alive) return;
            const names = [...((cfg && cfg.all_known) || []), ...((users || []).map((u) => u.name))];
            const match = names.find((n) => circuitSlug(n) === target);
            setResolved(match ? { circuit: match } : "redirect");
        });
        return () => { alive = false; };
    }, [sportName, slug]);

    if (resolved === "loading") {
        return <div className="min-h-[60vh] flex items-center justify-center text-[#a0a0a0]" data-testid="circuit-resolving">Loading…</div>;
    }
    if (resolved && resolved.circuit) return <Landing key={resolved.circuit} forcedCircuit={resolved.circuit} />;
    return <SlugRedirect />;
}

export default function SportSlugRouter() {
    const { slug } = useParams();
    const abbr = STATE_SLUG_TO_ABBR[String(slug || "").toLowerCase()];
    if (abbr) return <Landing key={abbr} forcedState={abbr} />;
    return <CircuitOrSlug />;
}
